import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ConfigurationPayload, FetchError, ResponseError } from "@xng/xng-backend";
import { API_XLOGS_BACKEND } from "../../api/api";
import { RootState } from "../store";
interface FeatureFlagsState {
  flags: Record<string, boolean>;
}

const initialState: FeatureFlagsState = {
  flags: {},
};

// The API answers 503 while it has no copy of the store yet; a cold start recovers within a retry or two.
const RETRY_DELAYS_MS = [1000, 2000, 4000];

function isTransient(error: unknown): boolean {
  if (error instanceof ResponseError) return error.response.status === 503;
  return error instanceof FetchError;
}

async function loadAccountConfiguration(clientId: string): Promise<ConfigurationPayload> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await API_XLOGS_BACKEND.getAccountConfiguration({ clientId });
    } catch (error) {
      if (attempt >= RETRY_DELAYS_MS.length || !isTransient(error)) throw error;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
    }
  }
}

export const initializeFeatureFlags = createAsyncThunk(
  "featureFlags/fetch",
  async (clientId: string) => {
    const configuration = await loadAccountConfiguration(clientId);
    return configuration.booleans ?? {};
  },
);

export const featureFlagsSlice = createSlice({
  name: "featureFlags",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        initializeFeatureFlags.fulfilled,
        (state, action: PayloadAction<Record<string, boolean>>) => {
          state.flags = action.payload;
        },
      )
      .addCase(initializeFeatureFlags.rejected, (state, action) => {
        console.error("Failed to fetch feature flags:", action.error);
      });
  },
});
export const selectFeatureFlags = (state: RootState, index: string) =>
  state.featureFlags.flags[index];

export default featureFlagsSlice.reducer;
